import { useEffect, useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { AtSign, Trash2, Loader2, Check } from "lucide-react";
import { toast } from "sonner";

function fmtDate(iso, locale) {
  try {
    return new Date(iso).toLocaleString(locale === "en" ? "en-GB" : "fr-FR", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch (_) {
    return iso;
  }
}

export const LoginAliasesPanel = () => {
  const { t, i18n } = useTranslation();
  const [aliases, setAliases] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [aliasEmail, setAliasEmail] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/login-aliases").then((r) => setAliases(r.data.aliases || [])).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    api.get("/admin/impersonate/candidates").then((r) => setCandidates(r.data.candidates || [])).catch(() => {});
  }, [load]);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q || selected) return [];
    return candidates
      .filter((c) => [c.full_name, c.email].filter(Boolean).some((s) => s.toLowerCase().includes(q)))
      .slice(0, 8);
  }, [candidates, query, selected]);

  const add = async () => {
    if (!selected || !aliasEmail.trim()) return;
    setSaving(true);
    try {
      await api.post("/admin/login-aliases", { member_id: selected.id, alias_email: aliasEmail.trim() });
      toast.success(t("audit.alias_added"));
      setSelected(null);
      setQuery("");
      setAliasEmail("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("audit.alias_error"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (a) => {
    try {
      await api.delete(`/admin/login-aliases/${a.id}`);
      toast.success(t("audit.alias_deleted"));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("audit.alias_error"));
    }
  };

  return (
    <section className="bg-white border border-border rounded-xl p-4 md:p-6" data-testid="audit-aliases-panel">
      <div className="flex items-center gap-3 mb-2">
        <AtSign className="w-5 h-5 text-apollo-orange" />
        <h2 className="font-heading font-bold text-lg tracking-tight">{t("audit.alias_title")}</h2>
      </div>
      <p className="text-sm text-text-secondary mb-4">{t("audit.alias_subtitle")}</p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-start mb-5">
        <div className="relative">
          <label className="text-xs uppercase tracking-wider text-text-secondary font-semibold block mb-1">
            {t("audit.alias_member")}
          </label>
          {selected ? (
            <button
              type="button"
              onClick={() => { setSelected(null); setQuery(""); }}
              data-testid="alias-selected-member"
              className="w-full h-10 px-3 flex items-center gap-2 rounded-md border border-apollo-orange bg-apollo-orange-light/40 text-sm text-left"
            >
              <Check className="w-4 h-4 text-apollo-orange flex-shrink-0" />
              <span className="truncate font-medium">{selected.full_name}</span>
            </button>
          ) : (
            <>
              <Input
                placeholder={t("audit.alias_search_member")}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                data-testid="alias-member-search"
              />
              {matches.length > 0 && (
                <div className="absolute z-20 mt-1 w-full bg-white border border-border rounded-md shadow-lg max-h-60 overflow-y-auto" data-testid="alias-member-results">
                  {matches.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setSelected(c)}
                      data-testid={`alias-member-pick-${c.id}`}
                      className="w-full px-3 py-2 text-left hover:bg-bg-soft text-sm"
                    >
                      <div className="font-medium truncate">{c.full_name}</div>
                      <div className="text-xs text-text-secondary truncate">{c.email} · {c.groupe_nom || c.region || ""}</div>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-text-secondary font-semibold block mb-1">
            {t("audit.alias_email_label")}
          </label>
          <Input
            type="email"
            placeholder="prenom@gmail.com"
            value={aliasEmail}
            onChange={(e) => setAliasEmail(e.target.value)}
            data-testid="alias-email-input"
          />
        </div>
        <div className="md:pt-6">
          <Button onClick={add} disabled={saving || !selected || !aliasEmail.trim()} data-testid="alias-add-btn" className="w-full md:w-auto">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            {t("audit.alias_add")}
          </Button>
        </div>
      </div>

      {aliases.length === 0 ? (
        <div className="text-sm text-text-secondary py-4 text-center" data-testid="alias-empty">{t("audit.alias_empty")}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="alias-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-text-secondary border-b border-border">
                <th className="px-3 py-2">{t("audit.alias_member")}</th>
                <th className="px-3 py-2">{t("audit.alias_group")}</th>
                <th className="px-3 py-2">{t("audit.alias_primary")}</th>
                <th className="px-3 py-2">{t("audit.alias_col")}</th>
                <th className="px-3 py-2">{t("audit.alias_date")}</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {aliases.map((a) => (
                <tr key={a.id} className="border-b border-border/50" data-testid={`alias-row-${a.id}`}>
                  <td className="px-3 py-2 font-medium">{a.member_full_name || a.user_id}</td>
                  <td className="px-3 py-2 text-xs">{a.groupe_nom || "-"}</td>
                  <td className="px-3 py-2 text-xs text-text-secondary">{a.member_email}</td>
                  <td className="px-3 py-2 font-mono text-xs">{a.alias_email}</td>
                  <td className="px-3 py-2 text-xs">{fmtDate(a.created_at, i18n.language)}</td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => remove(a)}
                      data-testid={`alias-delete-${a.id}`}
                      className="p-1.5 rounded hover:bg-red-50 text-red-600 transition-colors"
                      title={t("audit.alias_delete")}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};
